// @ts-nocheck
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, Loader2, Send } from "lucide-react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export const TriggerCoachImportTestEmails = () => {
  const [isSending, setIsSending] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('nancy+coachtest@scoutforce.com');
  const [sendingStep, setSendingStep] = useState<number | null>(null);

  const sendTestEmail = async (stepNumber: number) => {
    setSendingStep(stepNumber);
    setIsSending(true);
    try {
      // Get the Coach Onboarding Sequence
      const { data: sequence, error: seqError } = await supabase
        .from('email_sequences')
        .select('id, name')
        .ilike('name', '%Coach Onboarding%')
        .single();

      if (seqError || !sequence) {
        toast.error('Coach Onboarding Sequence not found');
        return;
      }

      // Get the step with linked template from email_sequence_steps
      const { data: step, error: stepError } = await supabase
        .from('email_sequence_steps')
        .select(`
          id,
          step_order,
          template_id,
          template:communication_templates (
            id,
            name,
            subject,
            html_content
          )
        `)
        .eq('sequence_id', sequence.id)
        .eq('step_order', stepNumber)
        .eq('is_active', true)
        .single();

      // Handle both array and single object returns from Supabase joins
      const templateData = step?.template;
      const template = Array.isArray(templateData) ? templateData[0] : templateData;
      
      if (stepError || !template?.html_content) {
        console.error('Step/template not found:', stepError);
        toast.error(`Step ${stepNumber} template not found`);
        return;
      }
      let subject = template.subject || `Step ${stepNumber}`;
      let htmlContent = template.html_content || '<p>No content</p>';

      // Replace merge tags with test values
      const testData = {
        first_name: 'TestCoach',
        coach_name: 'TestCoach',
        last_name: 'User',
        team_name: 'Test Team',
        accept_url: 'https://recruitlook.com/join-team?token=test123',
        login_url: 'https://recruitlook.com/auth',
        profile_url: 'https://recruitlook.com/profile',
      };

      subject = subject
        .replace(/\{\{first_name\}\}/g, testData.first_name)
        .replace(/\{\{coach_name\}\}/g, testData.coach_name)
        .replace(/\{\{last_name\}\}/g, testData.last_name);
      
      htmlContent = htmlContent
        .replace(/\{\{first_name\}\}/g, testData.first_name)
        .replace(/\{\{coach_name\}\}/g, testData.coach_name)
        .replace(/\{\{last_name\}\}/g, testData.last_name)
        .replace(/\{\{team_name\}\}/g, testData.team_name)
        .replace(/\{\{accept_url\}\}/g, testData.accept_url)
        .replace(/\{\{login_url\}\}/g, testData.login_url)
        .replace(/\{\{profile_url\}\}/g, testData.profile_url);

      console.log(`📧 Sending Step ${stepNumber} from template: ${template.name}`);

      // Send via send-email edge function
      const { data, error } = await invokeEdgeFunction('send-email', {
        body: {
          to_email: recipientEmail,
          subject: `[TEST Step ${stepNumber}] ${subject}`,
          html_content: htmlContent,
          email_type: 'custom_notification',
        }
      });

      if (error) {
        console.error('Error sending test email:', error);
        toast.error(`Failed to send Step ${stepNumber} email`);
        return;
      }

      toast.success(`Step ${stepNumber} test email sent to ${recipientEmail}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while sending test email');
    } finally {
      setIsSending(false);
      setSendingStep(null);
    }
  };

  const sendAllEmails = async () => {
    setIsSending(true);
    try {
      for (let step = 1; step <= 3; step++) {
        setSendingStep(step);
        await sendTestEmail(step);
        // Small delay between emails
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      toast.success('All 3 Coach Import test emails sent!');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSending(false);
      setSendingStep(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Test Coach Import Onboarding Sequence
        </CardTitle>
        <CardDescription>
          Send test emails from the Coach Onboarding Sequence to verify content
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recipient">Recipient Email</Label>
            <Input
              id="recipient"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="test@example.com"
            />
          </div>

          <div className="text-sm text-muted-foreground space-y-2 bg-muted/50 p-3 rounded-lg">
            <p><strong>Coach Onboarding Sequence Steps:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Email 1 (Day 0):</strong> Coaches: Your FREE Access to RecruitLook is Waiting</li>
              <li><strong>Email 2 (Day 3):</strong> Have you claimed your RecruitLook profile yet?</li>
              <li><strong>Email 3 (Day 7):</strong> Last chance to claim your RecruitLook profile</li>
            </ul>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button 
              variant="outline"
              onClick={() => sendTestEmail(1)} 
              disabled={isSending}
              className="w-full"
            >
              {sendingStep === 1 ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Email 1
            </Button>
            <Button 
              variant="outline"
              onClick={() => sendTestEmail(2)} 
              disabled={isSending}
              className="w-full"
            >
              {sendingStep === 2 ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Email 2
            </Button>
            <Button 
              variant="outline"
              onClick={() => sendTestEmail(3)} 
              disabled={isSending}
              className="w-full"
            >
              {sendingStep === 3 ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Email 3
            </Button>
          </div>

          <Button 
            onClick={sendAllEmails} 
            disabled={isSending}
            className="w-full"
          >
            {isSending && sendingStep === null ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            Send All 3 Test Emails
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
